import { useState } from 'react';
import {
  DISPUTE_REASON_LABELS,
  DISPUTE_REASONS,
  type DisputeReason,
  type InvoiceDetail,
} from '@faktorplus/shared';
import { useApproveInvoice, useDisputeInvoice } from '../../api/invoices';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/form';
import { ErrorBox } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import styles from './invoice-detail.module.css';

type Mode = 'idle' | 'disputing';

/**
 * The site manager's decision.
 *
 * Rejecting is deliberately as easy as approving: if pushing back were slower,
 * the fast path would win and the approval step would stop meaning anything.
 */
export function ManagerActions({ invoice, onDone }: { invoice: InvoiceDetail; onDone: () => void }) {
  const [mode, setMode] = useState<Mode>('idle');

  if (mode === 'disputing') {
    return (
      <DisputeForm invoice={invoice} onCancel={() => setMode('idle')} onSubmitted={onDone} />
    );
  }

  return <ApprovalButtons invoice={invoice} onApproved={onDone} onDispute={() => setMode('disputing')} />;
}

function ApprovalButtons({
  invoice,
  onApproved,
  onDispute,
}: {
  invoice: InvoiceDetail;
  onApproved: () => void;
  onDispute: () => void;
}) {
  const approve = useApproveInvoice();

  async function handleApprove() {
    await approve.mutateAsync(invoice.id);
    onApproved();
  }

  return (
    <div className={`${styles.actionCard} ${styles.actionCardPrimary}`}>
      <p className={styles.actionTitle}>Czy wszystko się zgadza?</p>

      {approve.error && (
        <ErrorBox>
          {approve.error instanceof ApiError
            ? approve.error.message
            : 'Nie udało się zapisać akceptacji.'}
        </ErrorBox>
      )}

      <div className={styles.actions}>
        <Button
          variant="success"
          large
          isLoading={approve.isPending}
          onClick={() => void handleApprove()}
        >
          Tak, zatwierdzam
        </Button>
        <Button variant="danger" large onClick={onDispute} disabled={approve.isPending}>
          Nie, zgłaszam uwagę
        </Button>
      </div>
    </div>
  );
}

function DisputeForm({
  invoice,
  onCancel,
  onSubmitted,
}: {
  invoice: InvoiceDetail;
  onCancel: () => void;
  onSubmitted: () => void;
}) {
  const [reason, setReason] = useState<DisputeReason | null>(null);
  const [comment, setComment] = useState('');
  const dispute = useDisputeInvoice();

  async function handleSubmit() {
    if (!reason) return;

    await dispute.mutateAsync({
      id: invoice.id,
      input: { reason, comment: comment.trim() || null },
    });
    onSubmitted();
  }

  return (
    <div className={`${styles.actionCard} ${styles.actionCardPrimary}`}>
      <p className={styles.actionTitle}>Co się nie zgadza?</p>

      <div className={styles.reasonList}>
        {DISPUTE_REASONS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={`${styles.reason} ${reason === candidate ? styles.reasonSelected : ''}`}
            aria-pressed={reason === candidate}
            onClick={() => setReason(candidate)}
          >
            {DISPUTE_REASON_LABELS[candidate]}
            {reason === candidate && <span aria-hidden="true">●</span>}
          </button>
        ))}
      </div>

      <Textarea
        rows={3}
        value={comment}
        placeholder="Opisz, co jest nie tak (opcjonalnie)"
        aria-label="Komentarz do zgłoszenia"
        onChange={(event) => setComment(event.target.value)}
      />

      {dispute.error && (
        <ErrorBox>
          {dispute.error instanceof ApiError
            ? dispute.error.message
            : 'Nie udało się wysłać zgłoszenia.'}
        </ErrorBox>
      )}

      <div className={styles.actions}>
        <Button
          variant="primary"
          large
          disabled={!reason}
          isLoading={dispute.isPending}
          onClick={() => void handleSubmit()}
        >
          Wyślij do księgowości
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={dispute.isPending}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}
